/**
 * Subscription plan contracts — mirrors `platform.subscription_plans`.
 *
 * Plans are global (not tenant-scoped) pricing tiers that tenants subscribe to.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Identifier
// ---------------------------------------------------------------------------

export const SubscriptionPlanIdSchema = z.string().uuid().brand<'SubscriptionPlanId'>();
export type SubscriptionPlanId = z.infer<typeof SubscriptionPlanIdSchema>;

// ---------------------------------------------------------------------------
// Record (output)
// ---------------------------------------------------------------------------

export const SubscriptionPlanSchema = z.object({
  id: SubscriptionPlanIdSchema,
  name: z.string().min(1).max(200),
  description: z.string().nullable(),
  maxEmployees: z.number().int().positive().nullable(),
  maxModules: z.number().int().positive().nullable(),
  priceMonthly: z.string(), // numeric as string from DB
  priceYearly: z.string(), // numeric as string from DB
  currency: z.string().length(3),
  isActive: z.boolean(),
  features: z.unknown().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

// ---------------------------------------------------------------------------
// Create input
// ---------------------------------------------------------------------------

export const CreateSubscriptionPlanInputSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  maxEmployees: z.number().int().positive().optional(),
  maxModules: z.number().int().positive().optional(),
  priceMonthly: z.number().min(0).default(0),
  priceYearly: z.number().min(0).default(0),
  currency: z.string().length(3).default('INR'),
  isActive: z.boolean().default(true),
  features: z.record(z.unknown()).optional(),
});
export type CreateSubscriptionPlanInput = z.infer<typeof CreateSubscriptionPlanInputSchema>;

// ---------------------------------------------------------------------------
// Update input
// ---------------------------------------------------------------------------

export const UpdateSubscriptionPlanInputSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().optional(),
  maxEmployees: z.number().int().positive().nullable().optional(),
  maxModules: z.number().int().positive().nullable().optional(),
  priceMonthly: z.number().min(0).optional(),
  priceYearly: z.number().min(0).optional(),
  currency: z.string().length(3).optional(),
  isActive: z.boolean().optional(),
  features: z.record(z.unknown()).nullable().optional(),
});
export type UpdateSubscriptionPlanInput = z.infer<typeof UpdateSubscriptionPlanInputSchema>;
